// Sankey helpers for the report chart adapter. Standalone copies of
// the chat artifact logic — analysis owns its rendering and must not
// import chat internals (ChartArtifact is a reference implementation,
// not a dependency).

export interface ResolvedSankeyLink {
  source: number
  target: number
  value: number
}

export function resolveSankeyLinks(
  nodes: { name: string }[],
  links: { source: string; target: string; value: number }[],
): ResolvedSankeyLink[] {
  const nameToIndex = new Map<string, number>()
  nodes.forEach((node, index) => {
    nameToIndex.set(node.name, index)
  })
  return links.flatMap((link) => {
    const source = nameToIndex.get(link.source)
    const target = nameToIndex.get(link.target)
    if (source === undefined || target === undefined) return []
    return [{ source, target, value: link.value }]
  })
}

// d3-sankey throws on cyclic input, so a cyclic flow must fall back
// instead of rendering.
export function hasSankeyCycle(
  nodeCount: number,
  links: ResolvedSankeyLink[],
): boolean {
  const adjacency = new Map<number, number[]>()
  for (const link of links) {
    const targets = adjacency.get(link.source) ?? []
    targets.push(link.target)
    adjacency.set(link.source, targets)
  }

  const UNSEEN = 0
  const IN_PROGRESS = 1
  const DONE = 2
  const state = new Array<number>(nodeCount).fill(UNSEEN)

  const visit = (node: number): boolean => {
    if (state[node] === IN_PROGRESS) return true
    if (state[node] === DONE) return false
    state[node] = IN_PROGRESS
    for (const next of adjacency.get(node) ?? []) {
      if (visit(next)) return true
    }
    state[node] = DONE
    return false
  }

  for (let node = 0; node < nodeCount; node++) {
    if (state[node] === UNSEEN && visit(node)) return true
  }
  return false
}

const MIN_SANKEY_HEIGHT = 400
const PIXELS_PER_NODE = 28
const SANKEY_VERTICAL_MARGIN = 40

// d3-sankey squeezes node padding to fit the height, so a tall column in a
// fixed 400px frame leaves 0px for the flows and the chart draws blank.
export function sankeyChartHeight(
  nodes: { name: string }[],
  links: ResolvedSankeyLink[],
): number {
  return Math.max(
    MIN_SANKEY_HEIGHT,
    tallestColumn(nodes, links) * PIXELS_PER_NODE + SANKEY_VERTICAL_MARGIN,
  )
}

function tallestColumn(
  nodes: { name: string }[],
  links: ResolvedSankeyLink[],
): number {
  const columnSizes = new Map<string, number>()
  const hasIncoming = new Set(links.map((link) => link.target))
  const hasOutgoing = new Set(links.map((link) => link.source))
  nodes.forEach((_node, index) => {
    const column = guessedColumn(hasIncoming.has(index), hasOutgoing.has(index))
    columnSizes.set(column, (columnSizes.get(column) ?? 0) + 1)
  })
  return Math.max(0, ...columnSizes.values())
}

function guessedColumn(hasIncoming: boolean, hasOutgoing: boolean): string {
  if (!hasIncoming) return 'sources'
  return hasOutgoing ? 'middles' : 'sinks'
}

export function sankeyLabelsByName(
  nodes: { name: string; label?: string }[],
): Map<string, string> {
  return new Map(nodes.map((node) => [node.name, node.label ?? node.name]))
}
